import { useQuery } from "@tanstack/react-query";
import { getLeakageSummary } from "@/api/audit";
import { useAuthStore } from "@/store/auth";
import type { ApiError } from "@/types";

export function LeakagePage() {
  const clinicId = useAuthStore((s) => s.clinicId);

  const summary = useQuery({
    queryKey: ["audit", "leakage", clinicId],
    queryFn: () => getLeakageSummary(clinicId!),
    enabled: Boolean(clinicId),
  });

  if (!clinicId) {
    return (
      <div>
        <div className="page-header">
          <h1>Leakage summary</h1>
        </div>
        <div className="card muted">
          No clinic ID set. Set <span className="mono">VITE_DEV_CLINIC_ID</span> in{" "}
          <span className="mono">.env</span> or log in to associate this session with a clinic.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>Leakage summary</h1>
        <p className="page-sub">Clinic-wide view of missed charges across all audited appointments.</p>
      </div>

      {summary.isLoading && (
        <div className="card loading">
          <div className="spinner" />
          <div>Loading summary…</div>
        </div>
      )}

      {summary.error && (
        <div className="card form-error">
          Failed to load summary: {(summary.error as unknown as ApiError).message}
        </div>
      )}

      {summary.data && (
        <>
          <div className="metrics">
            <div className="metric">
              <div className="label">Appointments audited</div>
              <div className="value">{summary.data.total_appointments_audited}</div>
            </div>
            <div className="metric critical">
              <div className="label">Total leakage</div>
              <div className="value">${summary.data.total_leakage_usd.toFixed(2)}</div>
            </div>
            <div className="metric">
              <div className="label">Avg / audit</div>
              <div className="value">${summary.data.average_leakage_per_audit.toFixed(2)}</div>
            </div>
            <div className="metric warning">
              <div className="label">Critical pending</div>
              <div className="value">{summary.data.critical_flags_pending}</div>
            </div>
          </div>

          <div className="card">
            <h2>Annualised projection</h2>
            <p className="muted">
              If current audit volume continues, the clinic is on track for approximately{" "}
              <strong style={{ color: "var(--red)" }}>
                ${(summary.data.average_leakage_per_audit * 365 * 30).toLocaleString("en-US", { maximumFractionDigits: 0 })}
              </strong>{" "}
              in annual leakage (based on {summary.data.total_appointments_audited} audits to date).
            </p>
          </div>

          <div className="card">
            <h2>Top missed items</h2>
            {summary.data.top_missed_items.length === 0 ? (
              <div className="muted">No critical flags recorded yet.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {summary.data.top_missed_items.map((item, i) => (
                  <div
                    key={i}
                    className="row"
                    style={{
                      padding: "12px 0",
                      borderBottom:
                        i < summary.data.top_missed_items.length - 1
                          ? "1px solid var(--border-subtle)"
                          : "none",
                    }}
                  >
                    <div className="grow">{item.description}</div>
                    <div className="mono muted">×{item.count}</div>
                    <div className="mono" style={{ minWidth: 80, textAlign: "right" }}>
                      ${item.estimated_value.toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
